<script>
import { GlobalPolicy } from '@/policies/global';
import { FormButton, SelectField, TextInput } from '../form';
import Media from '@/models/media';
import SelectMultipleField from '../form/SelectMultipleField.vue';
import MultiselectField from '../form/MultiselectField.vue';
import { KeywordsController } from '@/controllers/keywords';
import Keyword from '@/models/keyword';

export default {
    setup() {
        return {
            GlobalPolicy,
            Media
        }
    },
    data() {
        return {
            editing: false,
        }
    },
    methods: {
        edit() {
            this.editing = JSON.parse(JSON.stringify(this.item));
        },
        cancel() {
            this.editing = false;
        },
        save() {
            if(!this.valid) {
                return
            }
            this.$emit('update', this.editing);
            this.cancel();
        },
        getCollectionName(collectionId) {
            let collection = this.collections.find(c => c.community_rid === collectionId);
            return collection?.meta?.info?.name || collection?.kova?.MarketingName || "Not Found"
        },
        storeKeyword(keyword) {
            KeywordsController.store(new Keyword({
                name: keyword
            }))
            .then(resp => {
                this.keywords.push(resp);
                if (!this.editing.keywords) {
                    this.editing.keywords = [];
                }
                this.editing.keywords.push(resp.id);
            }) 
        },
    },
    computed: {
        singleCommunity: {
            get() {
                return this.editing.meta.communities?.[0] ?? null;
            },
            set(value) {
                this.editing.meta.communities = value ? [value] : [];
            }
        },
        singleCollection: {
            get() {
                return this.editing.meta.collections?.[0] ?? null;
            },
            set(value) {
                this.editing.meta.collections = value ? [value] : [];
            }
        },
        valid() {
            return this.editing.alt?.length > 0 &&
                this.editing.caption?.length > 0 &&
                this.editing.meta?.category !== null &&
                (
                    this.editing.meta.category !== 'home' ||
                    (
                        this.editing.meta.category === 'home' &&
                        this.editing.meta.position !== null
                    )
                )
        },
        collections() {
            if(this.communities.length == 0) return [];
                return this.editing.meta?.communities != null && this.editing.meta?.communities.length > 0 ?
                    this.communities.filter(community => this.editing.meta.communities.includes(community.id)).flatMap(community => community.collections) :
                    this.communities.flatMap(community => community.collections);
        },
        filtered() {
            let results = this.floorplans;
            if (this.editing.meta.collections != null && this.editing.meta.collections.length > 0) {
                let collections = this.communities.filter(item => this.editing.meta.communities.includes(item.id)).flatMap(community => community.collections.map(collection => collection.community_rid));
                results = results.filter(item => collections.includes(item.kova.CommunityRID));
            }

            let floorplans = [
                        ...new Map(
                            results
                            .filter(f => f.meta?.info?.name && f.kova?.ModelRID)
                            .map(f => [f.kova.ModelRID, {
                                id: f.kova.ModelRID,
                                name: f.meta.info.name
                            }])
                        ).values()
                    ];

            return floorplans;
        },
    },
    emits: ['update'],
    props: {
        item: {
            type: Object,
            required: true
        },
        communities: {
            type: Array,
            required: true
        },
        floorplans: {
            type: Array,
            required: true
        },
        keywords: {
            type: Array,
            required: true
        }
    },
    components: {
        FormButton,
        TextInput,
        SelectField,
        SelectMultipleField,
        MultiselectField
    },
    created() {
        if(this.$route.path.includes('/edit') && GlobalPolicy.edit()) {
            this.edit();
        }
    }
}
</script>

<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased ">Info</h2>
        <nav v-if="GlobalPolicy.edit()" class="flex items-end gap-2">
            <FormButton @click="edit()" v-if="!editing" icon="fa-pen" icon-size="text-lg" prefix-class="fak" color="blue">Edit</FormButton>
            <template v-else>
                <FormButton @click="cancel()" color="gray">Cancel</FormButton>
                <FormButton :disabled="!valid" @click="save" color="green">Save Changes</FormButton>
            </template>
        </nav>
    </div>
    <dl class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-if="!editing">
        <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Public Title</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">{{ item.alt }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Public Caption</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.caption }}</dd>
        <template v-if="item.keywords.length">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Keywords</dt>
            <dd class="text-sm text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-medium border-t-[2px] border-bhav-cool-gray-50">
                <ul class="flex items-center gap-2">
                    <li v-for="keywordId in item.keywords" :key="keywordId" class="rounded-md px-1 py-1 bg-bhav-orange-100 text-bhav-orange-500">{{ keywords.find(c => c.id === keywordId)?.name }}</li>
                </ul>
            </dd>
        </template>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Category</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.category?.label }}</dd>

        <template v-if="item.meta?.category == 'home'">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">View</dt>
            <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.meta.position }}</dd>
        </template>

        <template v-if="item.meta?.communities?.length ?? false">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Communities</dt>
            <dd class="text-sm text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
                <ul class="flex items-center gap-2">
                    <li v-for="communityId in item.meta.communities" :key="communityId" class="rounded-md px-1 py-1 bg-bhav-bright-blue-100 text-bhav-bright-blue-500">{{ communities.find(c => c.id === communityId)?.meta?.info?.name || communities.find(c => c.id === communityId)?.kova?.WebGroupName }}</li>
                </ul>
            </dd>
        </template>
        <template v-if="item.meta?.collections?.length ?? false">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Collections</dt>
            <dd class="text-sm text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
                <ul class="flex items-center gap-2">
                    <li v-for="collectionId in item.meta.collections" :key="collectionId" class="rounded-md px-1 py-1 bg-bhav-bright-blue-100 text-bhav-bright-blue-500">{{ getCollectionName(collectionId) }}</li>
                </ul>
            </dd>
        </template>

        <template v-if="item.meta?.floorplan ?? false">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Floorplan</dt>
            <dd class="text-sm text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
                <span class="rounded-md px-1 py-1 bg-bhav-bright-blue-100 text-bhav-bright-blue-500">{{ floorplans.find(c => c.kova.ModelRID === item.meta.floorplan)?.kova?.MarketingName }}</span>
            </dd>
        </template>

        <!-- <template v-if="item.meta?.elevation ?? false">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Elevation</dt>
            <dd class="text-sm leading-5  text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">
                <span class="rounded-md px-1 py-1 bg-bhav-bright-blue-100 text-bhav-bright-blue-500">{{ item.meta.elevation }}</span>
            </dd>
        </template>

        <template v-if="item.meta?.color_package !== null">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Color Package</dt>
            <dd class="text-sm text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">
                <span class="rounded-md px-1 py-1 bg-bhav-bright-blue-100 text-bhav-bright-blue-500">{{ item.meta.color_package }}</span>
            </dd>
        </template> -->
    </dl>

    <form @submit="save" class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-else>
        <legend class="sr-only">Media Item Properties</legend>

        <label for="editing.alt" class="font-normal text-bhav-blue-300 py-5">
            Public Title <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5">
            <TextInput placeholder="Like 'Kitchen' or 'Elevation A'" id="editing.alt" v-model="editing.alt" required maxlength="100" size="100" class="max-w-80" />
        </div>

        <label for="editing.caption" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Public Caption <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <textarea class="max-w-md bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-48" id="editing.caption" v-model="editing.caption" required maxlength="255"></textarea>
        </div>

        <label for="editing.meta.keywords" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Keywords
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <MultiselectField label="Add Keywords"
                :show-create="true"
                :multiple="true"
                @create="(e) => {storeKeyword(e)}"
                v-model="editing.keywords"
                color="orange"
                :options="this.keywords.map(keyword => ({ label: keyword.name, value: keyword.id}))" instructions="Select the keywords or create one"/>
        </div>

        <label for="editing.meta.category" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Category <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <MultiselectField label="Select Category" @update:model-value="(newValue) => { if(newValue !== 'home') {editing.meta.position = null} }" :show-search="false" :show-create="false" v-model="editing.meta.category" color="orange" :options="Media.categories" instructions="Select a Category"/>
        </div>
        <template v-if="editing.meta.category === 'home'">
            <label for="editing.meta.position" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                View <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
            </label>
            <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                <MultiselectField label="Select View"
                                        :show-search="false"
                                        :show-create="false"
                                        v-model="editing.meta.position"
                                        color="orange"
                                        :options="editing?.meta?.category != 'home' ? [] : Media.positions.map(position => ({ label: position, value: position}))" instructions="Select a View"/>
            </div>
        </template>

        <label for="editing.meta.category" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Communities
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <div class="inline-block">
                <MultiselectField label="Add Community"
                    v-model="singleCommunity"
                    :show-search="false"
                    :show-create="false"
                    :multiple="false"
                    color="blue"
                    @update:model-value="() => { singleCollection = null; editing.meta.floorplan = null; }"
                    :options="communities.map(community => ({ label: community.meta?.info?.name ?? community.kova?.WebGroupName, value: community.id }))"
                    instructions="Select a Community"/>
            </div>
        </div>

        <label for="editing.meta.category" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Collections
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <div class="inline-block">
                <MultiselectField label="Add Collection"
                    v-model="singleCollection"
                    :show-search="false"
                    :show-create="false"
                    :multiple="false"
                    min-list-width="min-w-96"
                    color="blue"
                    :disabled="editing.meta.communities == null || editing.meta.communities.length == 0"
                    @update:model-value="() => { editing.meta.floorplan = null }"
                    :options="collections.map(collection => ({label: collection.kova?.MarketingName, value: collection.community_rid}))"
                    instructions="Select a Collection"/>
            </div>
        </div>

        <label for="editing.meta.category" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Floorplan
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <div class="inline-block">
                <MultiselectField label="Add Floorplan"
                    v-model="editing.meta.floorplan"
                    :show-search="false"
                    :show-create="false"
                    :multiple="false"
                    :disabled="editing.meta.collections == null || editing.meta.collections.length == 0"
                    color="blue"
                    :options="filtered.map(item => ({ label: item.name, value: item.id }))"
                    instructions="Select a Floorplan"/>
            </div>
        </div>

        <!-- <label for="editing.alt" class="font-normal text-bhav-blue-300 border-t border-t-oakwood-gray-100 py-5">
            Elevation
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 lg:border-t border-t-oakwood-gray-100 py-5">
            <div class="inline-block">
                <MultiselectField label="Add Elevation"
                    v-model="editing.meta.elevation"
                    :show-search="false"
                    :show-create="false"
                    :multiple="false"
                    color="blue"
                    :options="[]"
                    instructions="Select an Elevation"/>
            </div>
        </div>

        <label for="editing.alt" class="font-normal text-bhav-blue-300 border-t border-t-oakwood-gray-100 py-5">
            Color Package
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 lg:border-t border-t-oakwood-gray-100 py-5">
            <div class="inline-block">
                <MultiselectField label="Add Color Package"
                    v-model="editing.meta.color_package"
                    :show-search="false"
                    :show-create="false"
                    :multiple="false"
                    color="blue"
                    :options="[]"
                    instructions="Select a Color Package"/>
            </div>
        </div> -->
    </form>
</template>